import type { QueryClient } from "@tanstack/react-query";
import { notFound, redirect } from "@tanstack/react-router";

import {
  buildLoginHrefWithRedirect,
  buildOnboardingHrefWithRedirect,
  buildSignupHrefWithRedirect,
  getRedirectPathFromLocation,
  type LoginRedirectLocation,
} from "@/lib/redirect-after-login";
import { getViewerContextForGuard } from "@/lib/viewer-context";

interface GuardOptions {
  queryClient?: QueryClient;
  location?: LoginRedirectLocation;
}

/**
 * Server-side onboarding guard — mirrors Next's `onboardingGuard()` but in the
 * TanStack `beforeLoad` world. Call from the `_app` pathless layout so every
 * authenticated user who hasn't completed onboarding is punted to `/onboarding`.
 *
 * Returns the session snapshot so child loaders can reuse it via
 * `context.session` without refetching.
 */
export async function onboardingGuard({ queryClient, location }: GuardOptions = {}) {
  const { user } = await getGuardSession(queryClient);
  if (user && !user.isAnonymous && !user.onboardingComplete) {
    throw redirect({
      href: buildOnboardingHrefWithRedirect(getRedirectPathFromLocation(location)),
    });
  }
  return { user };
}

/** Requires either a guest or permanent account; visitors start with Taste. */
export async function requireGuestOrMember({
  queryClient,
}: Pick<GuardOptions, "queryClient"> = {}) {
  const { user } = await getGuardSession(queryClient);
  if (!user) {
    throw redirect({ href: "/taste" });
  }
  return { user };
}

/**
 * Requires an authenticated session — redirects to `/login` if none.
 * Used by lists, notifications, and other auth-only leaf routes.
 */
export async function requireAuth({ queryClient, location }: GuardOptions = {}) {
  const { user } = await getGuardSession(queryClient);
  if (!user) {
    throw redirect({ href: buildLoginHrefWithRedirect(getRedirectPathFromLocation(location)) });
  }
  return { user };
}

/** Requires a permanent account; guests continue through the conversion flow. */
export async function requireMember({ queryClient, location }: GuardOptions = {}) {
  const { user } = await requireAuth({ queryClient, location });

  if (user.isAnonymous) {
    throw redirect({
      href: buildSignupHrefWithRedirect(getRedirectPathFromLocation(location)),
    });
  }

  return { user };
}

/** Requires a Better Auth admin role; non-admins receive the app's 404. */
export async function requireAdmin({ queryClient, location }: GuardOptions = {}) {
  const { user } = await requireAuth({ queryClient, location });

  if (!user.isAnonymous && user.role === "admin") {
    return { user };
  }

  throw notFound();
}

/**
 * Returns the session snapshot without redirecting — used by routes (community,
 * artist, profile) that want to conditionally prefetch based on auth state.
 */
export async function getOptionalSession({ queryClient }: Pick<GuardOptions, "queryClient"> = {}) {
  const { user } = await getGuardSession(queryClient);
  return { user };
}

async function getGuardSession(queryClient: QueryClient | undefined) {
  const viewer = await getViewerContextForGuard(queryClient);
  return { user: viewer.user };
}
